(function () {
  'use strict';

  const SELECTORS = {
    modal: '[data-article-modal]',
    backdrop: '[data-article-modal-backdrop]',
    close: '[data-article-modal-close]',
    title: '[data-article-modal-title]',
    body: '[data-article-modal-body]',
    image: '[data-article-modal-image]',
    hero: '.article-modal__hero',
    trigger: '.read-more-button, .article-card--modal-trigger'
  };

  const CLASSES = {
    bodyLocked: 'article-modal--body-locked'
  };

  let scrollY = 0;

  function getModal() {
    return document.querySelector(SELECTORS.modal);
  }

  function lockBodyScroll() {
    // Save current scroll position before locking
    scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;

    // Add class to body for CSS hooks and scroll lock
    document.body.classList.add(CLASSES.bodyLocked);
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    // Also lock html for iOS
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';

    // Store scroll position in data attribute for restoration
    document.body.dataset.scrollY = scrollY;
  }

  function unlockBodyScroll() {
    // Restore body
    document.body.classList.remove(CLASSES.bodyLocked);
    document.body.style.overflow = '';
    document.body.style.touchAction = '';

    // Restore html
    document.documentElement.style.overflow = '';
    document.documentElement.style.touchAction = '';

    // Restore scroll position
    const savedScrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    window.scrollTo(0, savedScrollY);

    // Clean up
    delete document.body.dataset.scrollY;
  }

  // Decode HTML entities that were escaped in data attributes
  function decodeHtmlEntities(html) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }

  function openModal(data) {
    const modal = getModal();
    if (!modal) return;

    const titleEl = modal.querySelector(SELECTORS.title);
    const bodyEl = modal.querySelector(SELECTORS.body);
    const imageEl = modal.querySelector(SELECTORS.image);
    const heroEl = modal.querySelector(SELECTORS.hero);

    // Decode HTML content before inserting
    const decodedContent = data.content ? decodeHtmlEntities(data.content) : '';

    // Populate content
    if (titleEl) titleEl.textContent = data.title || '';
    if (bodyEl) bodyEl.innerHTML = decodedContent;

    // Handle image
    if (imageEl) {
      if (data.image) {
        imageEl.src = data.image;
        imageEl.alt = data.imageAlt || data.title || '';
        imageEl.width = data.imageWidth || 0;
        imageEl.height = data.imageHeight || 0;

        // Set srcset if provided
        if (data.srcset) {
          imageEl.srcset = data.srcset;
          imageEl.sizes = '(min-width: 1200px) 550px, (min-width: 750px) calc((100vw - 130px) / 2), calc((100vw - 50px) / 2)';
        } else {
          imageEl.removeAttribute('srcset');
          imageEl.removeAttribute('sizes');
        }

        heroEl?.classList.remove('is-hidden');
        imageEl.style.display = 'block';
      } else {
        imageEl.src = '';
        imageEl.alt = '';
        imageEl.style.display = 'none';
        heroEl?.classList.add('is-hidden');
      }
    }

    // Lock body scroll FIRST (before showing modal)
    lockBodyScroll();

    // Show modal
    modal.setAttribute('aria-hidden', 'false');

    // Focus management - focus the title for screen readers (without scrolling)
    if (titleEl) {
      titleEl.setAttribute('tabindex', '-1');
      // Use preventScroll option to avoid automatic scrolling
      if (titleEl.focus) {
        titleEl.focus({ preventScroll: true });
      }
    }
  }

  function closeModal() {
    const modal = getModal();
    if (!modal) return;

    // Hide modal
    modal.setAttribute('aria-hidden', 'true');

    // Unlock body scroll
    unlockBodyScroll();
  }

  function getDataFromElement(el) {
    return {
      title: el.dataset.title || '',
      content: el.dataset.content || '',
      image: el.dataset.image || '',
      imageAlt: el.dataset.imageAlt || '',
      imageWidth: el.dataset.imageWidth || 0,
      imageHeight: el.dataset.imageHeight || 0,
      srcset: el.dataset.srcset || ''
    };
  }

  function handleTriggerClick(event) {
    // Prevent default to avoid any scroll/navigation issues
    event.preventDefault();
    event.stopPropagation();

    const btn = event.currentTarget;
    // Find the parent card that has the data attributes
    const card = btn.closest('.article-card--modal-trigger');
    const data = getDataFromElement(card || btn);
    openModal(data);
  }

  function handleCardClick(event) {
    // Only handle if the card itself is the trigger
    const card = event.currentTarget;
    if (!card || !card.classList.contains('article-card--modal-trigger')) return;

    // Don't trigger if clicking on an actual link or button inside the card
    if (event.target.closest('a[href]:not([href="#"])')) return;

    event.preventDefault();
    event.stopPropagation();

    const data = getDataFromElement(card);
    openModal(data);
  }

  function handleCardKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const card = event.currentTarget;
      const data = getDataFromElement(card);
      openModal(data);
    }
  }

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      const modal = getModal();
      if (modal && modal.getAttribute('aria-hidden') === 'false') {
        closeModal();
      }
    }
  }

  function init() {
    const modal = getModal();
    if (!modal) return;

    const backdrop = modal.querySelector(SELECTORS.backdrop);
    const closeBtn = modal.querySelector(SELECTORS.close);

    // Bind old trigger buttons (for backward compatibility)
    document.querySelectorAll('.read-more-button').forEach(function (btn) {
      btn.addEventListener('click', handleTriggerClick);
    });

    // Bind card triggers (new whole-card click)
    document.querySelectorAll('.article-card--modal-trigger').forEach(function (card) {
      card.addEventListener('click', handleCardClick);
      card.addEventListener('keydown', handleCardKeydown);
    });

    // Bind close interactions
    if (backdrop) {
      backdrop.addEventListener('click', handleBackdropClick);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    // Keyboard escape
    document.addEventListener('keydown', handleKeydown);

    // Re-bind for dynamically added content (simple delegation on document)
    document.addEventListener('click', function (event) {
      const btn = event.target.closest('.read-more-button');
      if (btn && !btn.closest('.article-card--modal-trigger')) {
        handleTriggerClick({ currentTarget: btn, preventDefault: function() {}, stopPropagation: function() {} });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
